import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { Badge, Button, Select } from '@artosera/ui';
import { ETATS_ACCES_EXTERNE, formatDate } from '@artosera/shared';
import { useAccesEspaces, type EtatAcces } from '../../api/espaceCollectionneur.js';
import { galerieALaction, galerieAModule } from '../../auth/sujet.js';
import { useShellContext } from '../shell/context.js';
import { NotFoundPage } from '../introuvable/NotFoundPage.js';
import styles from './EspaceCollectionneur.module.css';

export function EspacesPage() {
  const { galerie } = useShellContext();
  if (
    !galerieAModule(galerie, 'espace-collectionneur') ||
    !galerieALaction(galerie, 'collector-space:read')
  )
    return <NotFoundPage />;
  return <Espaces />;
}
function Espaces() {
  const { galerieSlug } = useShellContext();
  const { t, i18n } = useTranslation(['regie', 'commun']);
  const [etat, setEtat] = useState<EtatAcces | ''>('');
  const [page, setPage] = useState(1);
  const liste = useAccesEspaces(galerieSlug, etat, page);
  const date = (valeur: string | null) =>
    valeur
      ? formatDate(new Date(valeur), i18n.language === 'en' ? 'en' : 'fr')
      : t('espaceCollectionneur.jamais');
  return (
    <section className={styles.page}>
      <h1>{t('espaceCollectionneur.espaces')}</h1>
      <Select
        aria-label={t('espaceCollectionneur.filtreEtat')}
        value={etat || 'tous'}
        onValueChange={(v) => {
          setEtat(v === 'tous' ? '' : (v as EtatAcces));
          setPage(1);
        }}
        options={[
          { value: 'tous', label: t('espaceCollectionneur.tous') },
          ...ETATS_ACCES_EXTERNE.map((e) => ({
            value: e,
            label: t(`espaceCollectionneur.etats.${e}`),
          })),
        ]}
      />
      {liste.isLoading && <p role="status">{t('commun:chargement')}</p>}
      {liste.isError && <p role="alert">{t('commun:erreurReseau')}</p>}
      {liste.data && (
        <>
          <div className={styles.tableau}>
            <table>
              <caption>{t('espaceCollectionneur.espaces')}</caption>
              <thead>
                <tr>
                  {(['contact', 'etat', 'inviteLe', 'connexion', 'nombreElements'] as const).map(
                    (c) => (
                      <th key={c} scope="col">
                        {t(`espaceCollectionneur.colonnes.${c}`)}
                      </th>
                    ),
                  )}
                </tr>
              </thead>
              <tbody>
                {liste.data.acces.map((a) => (
                  <tr key={a.id}>
                    <td>
                      <Link to={`/${galerieSlug}/contacts/${a.contactId}`}>
                        {a.contactNom || t('espaceCollectionneur.contactEfface')}
                      </Link>
                    </td>
                    <td>
                      <Badge>{t(`espaceCollectionneur.etats.${a.etat}`)}</Badge>
                    </td>
                    <td>{date(a.inviteLe)}</td>
                    <td>{date(a.derniereConnexionLe)}</td>
                    <td>{a.elementsPartages}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {!liste.data.acces.length && <p>{t('espaceCollectionneur.aucunAcces')}</p>}
          {liste.data.pagination.pages > 1 && (
            <div className={styles.gestes}>
              <Button variant="secondary" disabled={page === 1} onClick={() => setPage(page - 1)}>
                {t('espaceCollectionneur.precedent')}
              </Button>
              <span>
                {t('espaceCollectionneur.pagination', { page, pages: liste.data.pagination.pages })}
              </span>
              <Button
                variant="secondary"
                disabled={page >= liste.data.pagination.pages}
                onClick={() => setPage(page + 1)}
              >
                {t('espaceCollectionneur.suivant')}
              </Button>
            </div>
          )}
        </>
      )}
    </section>
  );
}
